import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import format from "@app/lib/format";
import pkg from "@app/../package.json";
import { observer } from "mobx-react";
import { Button, Tooltip } from "antd";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { CopyOutlined, HighlightOutlined, BookOutlined, CommentOutlined } from "@ant-design/icons";
import { default as BookmarkModal } from "@app/components/document/bookmark/modal";
import { default as HighlightModal } from "@app/components/highlight/modal";
import { default as CommentsModal } from "@app/components/comments/modal";
import useClickOutside from "@app/hooks/useClickOutside";
import { renderToolbar } from "./toolbar";
import { copyHandler } from "./custom-copy";
import { ReactComponent as CopyLinkIcon } from "@app/assets/icons/copy-link.svg";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import "./style/pdf-viewer.scoped.scss";
import useSelection from "./useSelection";

import { highlightPlugin } from "./plugins/highlight/highlight-plugin";
import PulseCircle from "./plugins/highlight/pulse-circle";

const characterMap = {
    isCompressed: true,
    url: "/static/pdfjs/cmaps/2.6.347/",
};

export const PdfViewer = observer(
    ({
        fileId,
        goto,
        focus,
        renderPage,
        highlights = [],
        onBookmark,
        onAddHighlight,
        onComment,
        preventCopy,
        articleId,
        stage,
        articleType,
        documentId: propDocumentId,
    }) => {
        const currentHighlightAreasRef = useRef(null);
        // Keeps the reset scoped to this viewer, so nested viewers don't cancel each other
        const highlightingRef = useRef(null);

        const onClickOutsidePdf = () => {
            currentHighlightAreasRef.current = null;
            setCurrentHighlightAreas(null);
            if (highlightingRef.current) {
                highlightingRef.current.clearSelection();
            }
        };

        const [currentHighlightAreas, setCurrentHighlightAreas] = useState(null);

        const { project, document: urlDocumentId } = useParams();
        const documentId = propDocumentId || urlDocumentId;
        const pdfContainerRef = useClickOutside(onClickOutsidePdf);
        if (goto && focus) {
            const existing = highlights.find((hl) => {
                return hl.temp;
            });

            if (!existing || !existing.areas?.length) {
                highlights.push({ areas: [{ ...goto }], goto: true, temp: true });
            } else {
                const area = existing.areas[0] ?? {};
                if (area.left !== goto.left && area.top !== goto.top) {
                    existing.areas[0] = goto;
                }
            }
        } else if (!highlights.length && goto) {
            highlights.push({ areas: [{ ...goto, isPoint: true, _id: "1" }], goto: true });
        }

        const version = pkg.dependencies["pdfjs-dist"];
        const fileUrl = format.file(fileId);
        const [action, setAction] = useState(null);
        // Prevent repeated jump to the same target when component re-renders (e.g., after edits)
        const [gotoConsumed, setGotoConsumed] = useState(false);
        const toggleAction = useCallback((action, toggle) => {
            setAction(action);
            toggle();
        }, []);

        const defaultLayout = defaultLayoutPlugin({
            sidebarTabs: (defaultTabs) => [defaultTabs[0]],
            renderToolbar,
        });

        // Sanitize the annotations to remove invalid break characters
        const { onAnnotationLayerRender } = defaultLayout;
        defaultLayout.onAnnotationLayerRender = function (props) {
            try {
                onAnnotationLayerRender(props);
            } catch (ex) {
                // noop
            }
        };

        const selectedText = useSelection();
        const [doc, setDoc] = useState(null);
        const [highlight, setHighlight] = useState(null);
        const [bookmark, setBookmark] = useState(null);
        const [comment, setComment] = useState(null);

        const highlighting = highlightPlugin({
            renderHighlightTarget({ cancel, selectionRegion, toggle, highlightAreas }) {
                currentHighlightAreasRef.current = highlightAreas;
                setCurrentHighlightAreas(highlightAreas);

                const onCopyWithLink = async () => {
                    copyHandler({
                        selectedText,
                        highlightAreas: currentHighlightAreas,
                        project,
                        articleId,
                        stage,
                        articleType,
                    });
                    cancel();
                };

                const onCopyTextOnly = async () => {
                    copyHandler({
                        selectedText,
                        highlightAreas: currentHighlightAreas,
                        project,
                    });
                    cancel();
                };

                const onSourceDocCopyWithLink = async () => {
                    copyHandler({
                        selectedText,
                        highlightAreas: currentHighlightAreas,
                        project,
                        documentId,
                    });
                    cancel();
                };

                const isArticleContext = !!articleId;
                const isSourceDocContext = !isArticleContext && !!documentId;

                return (
                    <div
                        className={"tooltip-container"}
                        style={{
                            left: `${selectionRegion.left}%`,
                            top: `${selectionRegion.top + selectionRegion.height}%`,
                        }}
                    >
                        {!preventCopy && isArticleContext && (
                            <>
                                <Tooltip
                                    title={"Copy with link"}
                                    placement={"bottom"}
                                    mouseEnterDelay={0.5}
                                >
                                    <Button
                                        type={"text"}
                                        icon={<CopyLinkIcon className="copy-link-icon" />}
                                        onClick={onCopyWithLink}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title={"Copy text only"}
                                    placement={"bottom"}
                                    mouseEnterDelay={0.5}
                                >
                                    <Button
                                        type={"text"}
                                        icon={<CopyOutlined />}
                                        onClick={onCopyTextOnly}
                                    />
                                </Tooltip>
                            </>
                        )}
                        {!preventCopy && isSourceDocContext && (
                            <>
                                <Tooltip
                                    title={"Copy with link"}
                                    placement={"bottom"}
                                    mouseEnterDelay={0.5}
                                >
                                    <Button
                                        type={"text"}
                                        icon={<CopyLinkIcon className="copy-link-icon" />}
                                        onClick={onSourceDocCopyWithLink}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title={"Copy text only"}
                                    placement={"bottom"}
                                    mouseEnterDelay={0.5}
                                >
                                    <Button
                                        type={"text"}
                                        icon={<CopyOutlined />}
                                        onClick={onCopyTextOnly}
                                    />
                                </Tooltip>
                            </>
                        )}
                        {!preventCopy && !isArticleContext && !isSourceDocContext && (
                            <Tooltip title={"Copy"} placement={"bottom"} mouseEnterDelay>
                                <Button
                                    type={"text"}
                                    icon={<CopyOutlined />}
                                    onClick={onCopyTextOnly}
                                />
                            </Tooltip>
                        )}
                        {onAddHighlight && (
                            <Tooltip title={"Highlight"} placement={"bottom"} mouseEnterDelay>
                                <Button
                                    type={"text"}
                                    onClick={() => toggleAction("highlight", toggle)}
                                    icon={<HighlightOutlined />}
                                />
                            </Tooltip>
                        )}
                        {onBookmark && (
                            <Tooltip title={"Bookmark"} placement={"bottom"} mouseEnterDelay>
                                <Button
                                    type={"text"}
                                    onClick={() => toggleAction("bookmark", toggle)}
                                    icon={<BookOutlined />}
                                />
                            </Tooltip>
                        )}
                        {onComment && (
                            <Tooltip title={"Comment"} placement={"bottom"} mouseEnterDelay>
                                <Button
                                    type={"text"}
                                    onClick={() => toggleAction("comment", toggle)}
                                    icon={<CommentOutlined />}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },

            renderHighlightContent({ cancel, selectionRegion, selectedText, highlightAreas }) {
                setCurrentHighlightAreas(null);
                cancel();

                if (action === "highlight") {
                    setHighlight({
                        content: selectedText,
                        areas: highlightAreas,
                        source: "reactPdfViewer",
                    });
                }

                if (action === "bookmark") {
                    setBookmark({
                        areas: highlightAreas,
                        content: selectedText,
                    });
                }

                if (action === "comment") {
                    setComment({
                        areas: highlightAreas,
                        quotedText: selectedText,
                    });
                }

                setAction(null);
            },

            renderHighlights({ pageIndex, rotation, getCssProperties }) {
                return (
                    <div>
                        {highlights.map((highlight, i) => {
                            if (!highlight.areas) {
                                return null;
                            }

                            if (
                                highlight.areas.length === 1 &&
                                highlight.areas[0].isPoint &&
                                highlight.areas[0].pageIndex === pageIndex
                            ) {
                                return (
                                    <React.Fragment key={highlight._id}>
                                        <PulseCircle
                                            style={getCssProperties(highlight.areas[0], rotation)}
                                        />
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment key={highlight._id ?? i}>
                                    {highlight.areas
                                        .filter((area) => area.pageIndex === pageIndex)
                                        .map((area, index) => {
                                            return (
                                                <div
                                                    className={"highlight"}
                                                    key={`${highlight._id}-${index}`}
                                                    style={{
                                                        ...getCssProperties(area, rotation),
                                                    }}
                                                />
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}
                    </div>
                );
            },
        });
        const { jumpToHighlightArea } = highlighting;
        highlightingRef.current = highlighting;

        useEffect(() => {
            const onCopy = (event) => {
                return copyHandler({
                    event,
                    selectedText,
                    highlightAreas: currentHighlightAreasRef.current,
                    project,
                    documentId,
                    articleId,
                    stage,
                    articleType,
                });
            };

            document.addEventListener("copy", onCopy);

            return () => {
                document.removeEventListener("copy", onCopy);
            };
        }, [selectedText, project, documentId, articleId, stage, articleType]);

        useEffect(() => {
            // Only jump when:
            // - PDF doc is ready
            // - a goto target exists
            // - consumer requested focus (explicit user navigation)
            // - we have not already consumed this goto target
            if (doc && goto && !gotoConsumed) {
                jumpToHighlightArea(goto);
                setGotoConsumed(true);
            }
        }, [doc, goto, focus, gotoConsumed]);

        // If the goto prop changes to a different location, allow one more jump
        useEffect(() => {
            setGotoConsumed(false);
        }, [JSON.stringify(goto)]);

        useEffect(() => {
            // Stub effect. mobx needs to know we are watching something about this state to get update events
        }, [highlights.length]);

        // Fixed height required for viewer. See https://github.com/react-pdf-viewer/react-pdf-viewer/issues/775
        return (
            <div className="pdf-viewer" ref={pdfContainerRef}>
                <Worker workerUrl={`/static/pdfjs/worker/${version}/pdf.worker.min.js`}>
                    <Viewer
                        key={fileId}
                        fileUrl={fileUrl}
                        plugins={[defaultLayout, highlighting]}
                        onDocumentLoad={(ev) => setDoc(ev.doc)}
                        characterMap={characterMap}
                        renderPage={renderPage}
                    />
                </Worker>
                <HighlightModal
                    visible={!!highlight}
                    highlight={highlight}
                    onSave={(hl) => {
                        setHighlight(null);
                        onAddHighlight && onAddHighlight(hl);
                    }}
                    onDiscard={() => {
                        setHighlight(null);
                    }}
                />
                <BookmarkModal
                    visible={!!bookmark}
                    bookmark={bookmark}
                    onSave={(data) => {
                        setBookmark(null);
                        onBookmark && onBookmark(data);
                    }}
                    onDiscard={() => {
                        setBookmark(null);
                    }}
                />
                <CommentsModal
                    modalOpen={comment}
                    setModalOpen={() => setComment(null)}
                    onSubmitHandler={(data) => {
                        onComment && onComment({ ...data, ...comment });
                        setComment(null);
                    }}
                />
            </div>
        );
    },
);

export default PdfViewer;
